import { useEffect, useMemo, useState } from 'react';
import { listBatches } from '../../api/originatorApi';
import { DataTable } from '../../components/ui/DataTable';
import { StatusPill } from '../../components/ui/StatusPill';
import { Pagination } from '../../components/ui/Pagination';
import { KpiCard } from '../../components/ui/KpiCard';
import { Button } from '../../components/ui/Button';
import { SplitNavControl } from '../../components/ui/SplitNavControl';
import { IconClipboard, IconLayers, IconPackage, IconSearch, IconVault } from '../../components/ui/icons';
import { FEATURES } from '../../config/features';
import {
  originatorNextAction,
  originatorPackageAssetsPath,
  originatorPackageLoansPath,
  originatorPackagePath,
} from './originatorPackageLabels';

const STATUS_TABS = [
  { value: '', label: 'All' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'SUBMITTED', label: 'Submitted' },
  { value: 'CLASSIFIED', label: 'Classified' },
  { value: 'CERTIFIED', label: 'Certified' },
];

const NEXT_ACTION_TABS = [
  { value: '', label: 'All owners' },
  { value: 'originator', label: 'With you' },
  { value: 'provider', label: 'Provider review' },
];

function formatDateTime(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function PackagesPage() {
  const [statusFilter, setStatusFilter] = useState('');
  const [nextActionFilter, setNextActionFilter] = useState('');
  const [programFilter, setProgramFilter] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [packages, setPackages] = useState([]);
  const [total, setTotal] = useState(0);
  const [allStatesTotal, setAllStatesTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const programId = programFilter.trim();

  useEffect(() => {
    let cancelled = false;

    async function loadPackages() {
      setLoading(true);
      setError('');
      try {
        const [response, allStatesResponse] = await Promise.all([
          listBatches({
            page,
            pageSize,
            status: statusFilter || undefined,
            programId: programId || undefined,
          }),
          listBatches({ page: 1, pageSize: 1 }),
        ]);

        if (cancelled) return;
        setPackages(response.items || []);
        setTotal(response.meta?.total || 0);
        setAllStatesTotal(allStatesResponse.meta?.total || 0);
      } catch (requestError) {
        if (cancelled) return;
        setError(requestError.message || 'Unable to load packages.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadPackages();
    return () => {
      cancelled = true;
    };
  }, [page, pageSize, statusFilter, programId]);

  // Client-side refinement over the current page (owner + free-text), matching
  // the existing behavior. Server-side filters above drive pagination totals.
  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return packages.filter((item) => {
      if (
        nextActionFilter &&
        originatorNextAction(item).owner !== nextActionFilter
      ) {
        return false;
      }
      if (!term) return true;
      return (
        String(item.reference_number || '').toLowerCase().includes(term) ||
        String(item.id || '').toLowerCase().includes(term) ||
        String(item.program_id || '').toLowerCase().includes(term)
      );
    });
  }, [packages, nextActionFilter, search]);

  const columns = useMemo(() => {
    const cols = [
      {
        key: 'sr',
        header: 'Sr',
        render: (row) => (
          <span className="text-eorig-muted">{row.__sr}</span>
        ),
      },
      {
        key: 'batch',
        header: 'Batch',
        sortValue: (row) => row.reference_number || row.id,
        render: (row) => (
          <div className="min-w-0">
            <span className="block font-semibold text-eorig-ink">
              {row.reference_number || row.id}
            </span>
            <span className="block text-[11px] text-eorig-muted">
              {formatDateTime(row.created_at)}
              {FEATURES.sourceChannel && row.source_channel
                ? ` · ${row.source_channel}`
                : ''}
            </span>
          </div>
        ),
      },
      {
        key: 'program_id',
        header: 'Program',
        filter: {
          type: 'text',
          value: programFilter,
          placeholder: 'Program UUID',
          ariaLabel: 'Filter by program',
          onChange: (value) => {
            setPage(1);
            setProgramFilter(value);
          },
        },
        render: (row) =>
          row.program_id ? (
            <span className="inline-flex max-w-[160px] truncate rounded-md bg-eorig-surface px-2 py-1 font-mono text-[11px] text-eorig-muted">
              {row.program_id}
            </span>
          ) : (
            <span className="text-eorig-muted">—</span>
          ),
      },
      {
        key: 'package_status',
        header: 'Status',
        filter: {
          type: 'select',
          value: statusFilter,
          options: STATUS_TABS,
          ariaLabel: 'Filter by status',
          onChange: (value) => {
            setPage(1);
            setStatusFilter(value);
          },
        },
        render: (row) => <StatusPill value={row.package_status} />,
      },
      {
        key: 'next_action',
        header: 'Next action',
        sortValue: (row) => originatorNextAction(row).label,
        filter: {
          type: 'select',
          value: nextActionFilter,
          options: NEXT_ACTION_TABS,
          ariaLabel: 'Filter by next-action owner',
          onChange: setNextActionFilter,
        },
        render: (row) => {
          const nextAction = originatorNextAction(row);
          return (
            <StatusPill
              value={nextAction.label}
              tone={nextAction.tone}
              label={nextAction.label}
            />
          );
        },
      },
      {
        key: 'upload_status',
        header: 'Upload',
        render: (row) => (
          <span className="text-eorig-muted">{row.upload_status || '—'}</span>
        ),
      },
      {
        key: 'open',
        header: '',
        sortable: false,
        align: 'right',
        className: 'whitespace-nowrap',
        headerClassName: 'w-px',
        render: (row) => {
          const auditTo = originatorPackagePath(row.id);
          return (
            <SplitNavControl
              ariaLabel={`Open ${row.reference_number || row.id}`}
              items={[
                {
                  to: auditTo,
                  label: 'Audit',
                  icon: IconClipboard,
                  disabled: !auditTo,
                },
                {
                  to: originatorPackageAssetsPath(row.id),
                  label: 'Assets',
                  icon: IconVault,
                  disabled: !originatorPackageAssetsPath(row.id),
                },
                {
                  to: originatorPackageLoansPath(row.id),
                  label: 'Loans',
                  icon: IconLayers,
                },
              ]}
            />
          );
        },
      },
    ];
    return cols;
  }, [nextActionFilter, programFilter, statusFilter]);

  const rows = useMemo(
    () =>
      filteredRows.map((row, index) => ({
        ...row,
        __sr: (page - 1) * pageSize + index + 1,
      })),
    [filteredRows, page, pageSize]
  );

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          label="Total packages"
          value={allStatesTotal}
          subline="Across all states"
          icon={IconPackage}
          accent
          loading={loading}
        />
        {FEATURES.packageStateCounts ? (
          <>
            <KpiCard label="Ready to certify" value={0} loading={loading} />
            <KpiCard label="Awaiting authority" value={0} loading={loading} />
            <KpiCard label="Admitted" value={0} loading={loading} />
          </>
        ) : null}
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-eorig-muted">
            <IconSearch className="h-4 w-4" />
          </span>
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search this page by batch or program"
            className="w-full rounded-full border border-eorig-border bg-white py-2 pl-9 pr-3 text-sm text-eorig-ink placeholder:text-eorig-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-eorig-cyan/35"
          />
        </div>
        {FEATURES.listExports ? (
          <Button variant="ghost">Export</Button>
        ) : null}
      </div>

      {error ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
          {error}
        </div>
      ) : null}

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        loading={loading}
        emptyMessage="No packages found for selected filters."
        minWidth={880}
      />

      <Pagination
        page={page}
        pageSize={pageSize}
        total={total}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPage(1);
          setPageSize(size);
        }}
        disabled={loading}
      />
    </div>
  );
}
